/**
 * "خروجی" for V2DataGrid list tables: the rows the user currently sees
 * (after search/filters), as CSV. UTF-8 with a BOM so Excel opens Persian
 * text correctly. Deliberately not built on lib/dataImport/xlsxWorkbook.ts —
 * that module pulls exceljs into every page that would import it.
 */
export interface CsvColumn<Row> {
  readonly header: string;
  readonly value: (row: Row) => string | number | null | undefined;
}

/**
 * CSV/formula injection (S9): a text cell that begins with `=`, `+`, `-`, `@`,
 * TAB or CR is executed as a formula by Excel / Sheets when the file is
 * opened. Prefixing a single quote makes the spreadsheet treat it as text. Only
 * the export is touched — stored values stay raw. Real numbers (`typeof
 * number`) and plain signed decimals such as `-12.5` are left alone so negative
 * amounts remain numeric.
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/;
const PLAIN_NUMBER = /^[+-]?\d+(\.\d+)?$/;

export function neutralizeFormula(text: string): string {
  return FORMULA_LEAD.test(text) && !PLAIN_NUMBER.test(text) ? `'${text}` : text;
}

function csvCell(v: string | number | null | undefined): string {
  const s =
    v === null || v === undefined ? '' : typeof v === 'number' ? String(v) : neutralizeFormula(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv<Row>(columns: readonly CsvColumn<Row>[], rows: readonly Row[]): string {
  const lines = [columns.map((c) => csvCell(c.header)).join(',')];
  for (const row of rows) lines.push(columns.map((c) => csvCell(c.value(row))).join(','));
  return lines.join('\r\n');
}

export function downloadCsv<Row>(
  filename: string,
  columns: readonly CsvColumn<Row>[],
  rows: readonly Row[],
): void {
  const blob = new Blob(['﻿', toCsv(columns, rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
